<template>
	<div class="job-deck">
		<div class="job-deck-stack">
			<div v-if="variants.length > 2" class="job-deck-card job-deck-card-back" aria-hidden="true" />

			<div class="job-deck-card job-deck-card-mid" aria-hidden="true" />

			<transition :name="transition_name" mode="out-in">
				<div :key="current_index" class="job-deck-card job-deck-card-front">
					<job
						:item="current_variant"
						:hide_summary="hide_summary"
						:hide_apply_button="hide_apply_button"
						:logo_url_template="logo_url_template"
						:disable_modal="disable_modal"
					>
						<template #deck-footer>
							<footer class="job-deck-footer">
								<v-icon slug="briefcase-blank" />

								<span v-if="item.company_name">{{ deck_label }} {{ item.company_name }}</span>

								<div class="job-deck-nav" :class="{ 'is-loading': loading }" :aria-busy="loading">
									<button
										type="button"
										class="job-deck-nav-btn"
										aria-label="Previous job"
										:disabled="current_index === 0"
										@click="prev"
									>
										<v-icon slug="angle-right" class="flip" />
									</button>

									<span aria-live="polite" aria-atomic="true"
										><strong>{{ current_index + 1 }}</strong> of {{ total }}</span
									>

									<button type="button" class="job-deck-nav-btn" aria-label="Next job" @click="next">
										<v-icon slug="angle-right" />
									</button>
								</div>
							</footer>
						</template>
					</job>
				</div>
			</transition>
		</div>
	</div>
</template>

<style lang="scss" scoped>
@use '/sass/variables' as *;

.job-deck {
	display: flex;
	flex-direction: column;
	min-width: 0;
}

.job-deck-stack {
	position: relative;
}

.job-deck-card {
	border-radius: $spacer;
}

.job-deck-card-back,
.job-deck-card-mid {
	position: absolute;
	top: 0;
	right: 0;
	left: 0;
	height: 100%;
	background-color: $white;
	border: 1px solid $grey-border;
	pointer-events: none;
}

.job-deck-card-mid {
	top: 0.5em;
	right: 0.5em;
	left: 0.5em;
	z-index: 1;
}

.job-deck-card-back {
	top: 1em;
	right: 1em;
	left: 1em;
	z-index: 0;
}

.job-deck-card-front {
	position: relative;
	z-index: 2;
}

.job-deck-next-enter-active,
.job-deck-next-leave-active,
.job-deck-prev-enter-active,
.job-deck-prev-leave-active {
	transition:
		transform 0.3s ease,
		opacity 0.3s ease;
}

.job-deck-next-enter-from {
	transform: translateX(2em) rotate(3deg);
	opacity: 0;
}

.job-deck-next-leave-to {
	transform: translateX(-2em) rotate(-3deg);
	opacity: 0;
}

.job-deck-prev-enter-from {
	transform: translateX(-2em) rotate(-3deg);
	opacity: 0;
}

.job-deck-prev-leave-to {
	transform: translateX(2em) rotate(3deg);
	opacity: 0;
}

.job-deck-footer {
	display: flex;
	align-items: center;
	gap: 0.5em;
	margin: 1em (-$spacer) (-$spacer);
	padding: 1em $spacer;
	border-top: 1px solid $grey-border;
	background-color: $orange-light;
	border-radius: 0 0 $spacer $spacer;
	font-size: 0.75rem;
	font-weight: bold;

	> svg {
		display: block;
		flex: 0 0 auto;
		width: 1em;
		height: 1em;
	}

	> span {
		flex: 1 1 auto;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
}

.job-deck-nav {
	display: flex;
	flex: 0 0 auto;
	align-items: center;
	gap: 0.5em;
	white-space: nowrap;
	transition: opacity 0.2s ease;

	&.is-loading {
		opacity: 0.5;
	}
}

.job-deck-nav-btn {
	display: flex;
	flex: 0 0 auto;
	padding: 0;
	border: none;
	background: none;
	cursor: pointer;
	width: 1.5em;
	height: 1.5em;
	border: 1px solid $grey-border;
	background-color: $white;
	justify-content: center;
	align-items: center;
	border-radius: 50%;

	&:disabled {
		cursor: default;
		opacity: 0.4;
	}

	svg {
		display: block;
		width: 0.75em;
		height: 0.75em;

		&.flip {
			transform: scaleX(-1);
		}
	}
}
</style>

<script setup>
import { notify } from 'notify';
import { computed, onBeforeUnmount, ref } from 'vue';

import Job from './_job.vue';
import VIcon from './_v-icon.vue';

const props = defineProps({
	item: {
		type: Object,
		required: true,
	},
	load_page: {
		type: Function,
		default: null,
	},
	hide_summary: {
		type: Boolean,
	},
	hide_apply_button: {
		type: Boolean,
	},
	logo_url_template: {
		type: String,
		required: false,
		default: null,
	},
	disable_modal: {
		type: Boolean,
	},
});

const current_index = ref(0);

/**
 * The jobs the deck can currently navigate: the hydrated `variant_preview`
 * plus any pages appended by `load_page`.
 */
const variants = ref([...props.item.variant_preview]);

const loading = ref(false);

const transition_name = ref(`job-deck-next`);

// Promise for the in-flight page fetch so navigation awaits a prefetch that
// started earlier rather than issuing a second request.
let pending_page = null;

// True while a click is blocked waiting for a page, guarding rapid re-clicks.
let navigating = false;

// Aborts any in-flight page fetch when the deck unmounts (e.g. filters change).
const controller = new AbortController();

const total = computed(() => {
	if (!props.load_page) {
		return variants.value.length;
	}

	return Math.max(props.item.variant_count || 0, variants.value.length);
});

const current_variant = computed(() => variants.value[current_index.value]);

const deck_label = computed(() => (props.item.is_viewed_company ? `More jobs at` : `Similar jobs at`));

function append_page(loaded) {
	for (const job of loaded || []) {
		if (variants.value.some((variant) => variant.uuid === job.uuid)) {
			continue;
		}

		variants.value.push(job);
	}
}

/* eslint-disable require-atomic-updates -- single-threaded guards: these flags
   are only mutated here and read synchronously before the fetch resolves. */
function load_next_page() {
	if (!props.load_page || variants.value.length >= total.value) {
		return Promise.resolve();
	}

	if (pending_page) {
		return pending_page;
	}

	loading.value = true;

	pending_page = props
		.load_page(variants.value, controller.signal)
		.then((loaded) => append_page(loaded))
		.catch((error) => {
			if (error?.name !== `AbortError`) {
				notify(`error`, error);
			}
		})
		.finally(() => {
			loading.value = false;
			pending_page = null;
		});

	return pending_page;
}

// Start fetching the next page as soon as the deck reaches its last loaded job,
// so the request overlaps the time the user spends reading it.
function prefetch_page() {
	if (current_index.value >= variants.value.length - 1) {
		load_next_page();
	}
}

async function next() {
	if (navigating) {
		return;
	}

	transition_name.value = `job-deck-next`;

	if (current_index.value < variants.value.length - 1) {
		current_index.value++;
		prefetch_page();
		return;
	}

	navigating = true;

	try {
		await load_next_page();
	} finally {
		navigating = false;
	}

	if (current_index.value < variants.value.length - 1) {
		current_index.value++;
		prefetch_page();
		return;
	}

	// End of every matching job: loop back to the first.
	current_index.value = 0;
}

/* eslint-enable require-atomic-updates */

function prev() {
	if (current_index.value === 0) {
		return;
	}

	transition_name.value = `job-deck-prev`;
	current_index.value--;
}

onBeforeUnmount(() => controller.abort());
</script>
